import { styled } from '@mui/system';
import ourProductLogo from '../../../assets/icons/our-product.png';
import backstageLogo from '../../../assets/icons/backstage.svg';
import { Typography } from "@mui/material";
import comparatorArrow from '../../../assets/icons/comparator-arrow.svg';
import { Grid } from "../../common/Grid/Grid";
import { GridItem } from "../../common/Grid/GridItem";
import openSource from '../../../assets/icons/open-source.svg';
import updated from '../../../assets/icons/updated.svg';
import popularity from '../../../assets/icons/popularity.svg';
import security from '../../../assets/icons/security.svg';
import accessibility from '../../../assets/icons/accessibility.svg';
import modular from '../../../assets/icons/modular.svg';
import ux from '../../../assets/icons/ux.svg';
import { SectionTitle } from "../../common/SectionTitle/SectionTitle";
import onboarding from '../../../assets/icons/onboarding.svg';
import incidents from '../../../assets/icons/incidents.svg';
import deployment from '../../../assets/icons/deployment.svg';
import developmentTime from '../../../assets/icons/development-time.svg';
import { ProductCard } from "./ProductCard";
import { RoiCard } from "./RoiCard";
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';

const OurProductContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '76px',
    '@media (max-width: 1024px)': {
        marginTop: '48px',
        padding: '0 16px',
    }
});


const OurProductDescriptionContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '96px',
    '@media (max-width: 1024px)': {
        flexDirection: 'column',
        gap: '24px',
        marginBottom: '48px',
    }
});

const OurProductDescription = styled(Typography<'p'>)<{ color: string, fontWeight: string }>(({ color, fontWeight }) => ({
    color: color,
    fontSize: '16px',
    fontWeight: fontWeight,
    lineHeight: '24px',
    textAlign: 'left',
    maxWidth: '45%',
    '@media (max-width: 1024px)': {
        maxWidth: '100%',
        textAlign: 'center',

    }
}));

const ComparatorContainerDesktop = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: '49px',
});

const ComparatorContainerMobile = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: '32px',
    gap: '24px',
});

const BackstageLogo = styled('img')({
    width: '310px',
    height: '64px',
    marginRight: '54px',
    '@media (max-width: 1024px)': {
        width: '200px',
        height: 'auto',
        marginRight: '0',
    }
});

const ComparatorArrow = styled('img')({
    width: '64px',
    height: '64px',
    '@media (max-width: 1024px)': {
        transform: 'rotate(90deg)',
    }
});

const AstrolabeText = styled(Typography<'p'>)({
    color: '#00E378',
    textAlign: 'center',
    fontSize: '48px',
    fontStyle: 'normal',
    fontWeight: '800',
    lineHeight: '91px',
    marginLeft: '74px',
    '@media (max-width: 1024px)': {
        fontSize: '32px',
        lineHeight: '48px',
        marginLeft: '0',
    }
});

export const OurProduct = () => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:1024px)');

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const renderComparisonCards = () => {
        const backstageCards = (
            <>
                <GridItem columnSpan={2} rowSpan={1} columnSpanMobile={2} rowSpanMobile={1}>
                    <motion.div variants={itemVariants}>
                        <ProductCard
                            icon={openSource}
                            iconAlt="open-source"
                            title={t('ourProduct.cards.openSource.title')}
                            color="#F1F7F7"
                            description={t('ourProduct.cards.openSource.description')}
                            padding="48px 88px 24px 88px"
                        />
                    </motion.div>
                </GridItem>
                <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                    <motion.div variants={itemVariants}>
                        <ProductCard
                            icon={modular}
                            iconAlt="modular"
                            title={t('ourProduct.cards.modular.title')}
                            color="#F1F7F7"
                            description={t('ourProduct.cards.modular.description')}
                            padding="39px 24px"
                        />
                    </motion.div>
                </GridItem>
                <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                    <motion.div variants={itemVariants}>
                        <ProductCard
                            icon={popularity}
                            iconAlt="popularity"
                            title={t('ourProduct.cards.popularity.title')}
                            color="#F1F7F7"
                            description={t('ourProduct.cards.popularity.description')}
                            padding="39px 24px"
                        />
                    </motion.div>
                </GridItem>
            </>
        );

        const astrolabeCards = (
            <>
                <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                    <motion.div variants={itemVariants}>
                        <ProductCard
                            icon={security}
                            iconAlt="security"
                            title={t('ourProduct.cards.security.title')}
                            color="#00E378"
                            description={t('ourProduct.cards.security.description')}
                            subDescription={t('ourProduct.cards.security.subDescription')}
                            padding="39px 24px"
                        />
                    </motion.div>
                </GridItem>
                <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                    <motion.div variants={itemVariants}>
                        <ProductCard
                            icon={ux}
                            iconAlt="ux"
                            title={t('ourProduct.cards.ux.title')}
                            color="#00E378"
                            description={t('ourProduct.cards.ux.description')}
                            padding="39px 24px"
                        />
                    </motion.div>
                </GridItem>
                <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                    <motion.div variants={itemVariants}>
                        <ProductCard
                            icon={accessibility}
                            iconAlt="accessibility"
                            title={t('ourProduct.cards.accessibility.title')}
                            color="#00E378"
                            description={t('ourProduct.cards.accessibility.description')}
                            padding="39px 24px"
                        />
                    </motion.div>
                </GridItem>
                <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                    <motion.div variants={itemVariants}>
                        <ProductCard
                            icon={updated}
                            iconAlt="updated"
                            title={t('ourProduct.cards.updated.title')}
                            color="#00E378"
                            description={t('ourProduct.cards.updated.description')}
                            padding="39px 24px"
                        />
                    </motion.div>
                </GridItem>
            </>
        );

        if (isMobile) {
            return (
                <>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <ComparatorContainerMobile>
                            <BackstageLogo src={backstageLogo} alt="Backstage" />

                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                style={{ width: '100%' }}
                            >
                                <Grid columns={4} rows={1} mobileColumns={2} mobileRows={2}>
                                    {backstageCards}
                                </Grid>
                            </motion.div>

                            <ComparatorArrow src={comparatorArrow} alt="Comparator Arrow" />
                            <AstrolabeText component="p">Astrolabe</AstrolabeText>

                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                style={{ width: '100%' }}
                            >
                                <Grid columns={4} rows={1} mobileColumns={2} mobileRows={2}>
                                    {astrolabeCards}
                                </Grid>
                            </motion.div>
                        </ComparatorContainerMobile>
                    </motion.div>
                </>
            );
        }

        return (
            <>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <ComparatorContainerDesktop>
                        <BackstageLogo src={backstageLogo} alt="Backstage" />
                        <ComparatorArrow src={comparatorArrow} alt="Comparator Arrow" />
                        <AstrolabeText component="p">Astrolabe</AstrolabeText>
                    </ComparatorContainerDesktop>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    <Grid columns={4} rows={2} mobileColumns={2} mobileRows={4}>
                        {backstageCards}
                        {astrolabeCards}
                    </Grid>
                </motion.div>
            </>
        );
    };

    return (
        <OurProductContainer>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
            >
                <SectionTitle title={t('ourProduct.title')} logo={ourProductLogo} />
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
            >
                <OurProductDescriptionContainer>
                    <OurProductDescription component="p" color="#F1F7F7" fontWeight="400">
                        {t('ourProduct.description1')}
                    </OurProductDescription>
                    <OurProductDescription component="p" color="#40EA9A" fontWeight="700">
                        {t('ourProduct.description2')}
                    </OurProductDescription>
                </OurProductDescriptionContainer>
            </motion.div>

            {renderComparisonCards()}

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
            >
                <SectionTitle title={t('ourProduct.roi.title')} description={t('ourProduct.roi.subtitle')} />
            </motion.div>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <Grid columns={4} rows={1} mobileColumns={2} mobileRows={2}>
                    <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                        <motion.div variants={itemVariants}>
                            <RoiCard icon={onboarding} stat="40%" description={t('ourProduct.roi.onboarding')} />
                        </motion.div>
                    </GridItem>

                    <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                        <motion.div variants={itemVariants}>
                            <RoiCard icon={incidents} stat="70%" description={t('ourProduct.roi.incidents')} />
                        </motion.div>
                    </GridItem>

                    <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                        <motion.div variants={itemVariants}>
                            <RoiCard icon={deployment} stat="60%" description={t('ourProduct.roi.deployment')} />
                        </motion.div>
                    </GridItem>

                    <GridItem columnSpan={1} rowSpan={1} columnSpanMobile={1} rowSpanMobile={1}>
                        <motion.div variants={itemVariants}>
                            <RoiCard icon={developmentTime} stat="25%" description={t('ourProduct.roi.development')} backgroundColor="#00E378" statColor="#F1F7F7" />
                        </motion.div>
                    </GridItem>
                </Grid>
            </motion.div>
        </OurProductContainer>
    );
};
